<template>
  <!-- data-hotkey-capture: marks the recording field so the app-wide
       automation-hotkey listener (useAutomationHotkeys.ts) ignores key
       events originating here while a combo is being redefined. Bound via
       v-bind because vue-tsc rejects unknown static attributes. -->
  <div class="hotkey-capture" v-bind="{ 'data-hotkey-capture': '' }">
    <FormInput
      ref="hotkeyInput"
      type="text"
      :size="size"
      :placeholder="SymbolicRegisteredHotkey"
      :value="SymbolicCapturingHotkey"
      @keydown.prevent="onKeyPressed"
      @keyup.prevent="onKeyReleased"
    />
    <BaseButton
      v-if="registeredHotkey"
      kind="auxiliary"
      type="tertiary"
      @click="clear"
    >
      <template #icon>
        <CloseIcon />
      </template>
    </BaseButton>
  </div>
</template>

<script lang="ts">
/**
 * Generic hotkey capture component
 *
 * A pure, controlled capture field: it displays the hotkey given by the
 * `value` prop and emits `update:value` with the captured key sequence (or
 * null when cleared). It performs no storage or registration of its own —
 * the parent owns those:
 * - GlobalHotkeyCapture.vue registers Electron OS-global shortcuts through
 *   `hotkeys.ipc` (settings screens);
 * - Automation.vue keeps the in-app automation hotkey as form state,
 *   persisted per account via useAutomationHotkeys.
 *
 * Keys are captured by physical position (`KeyboardEvent.code`, named after
 * US QWERTY — see keymap.ts), and displayed with the labels of the user's
 * active keyboard layout.
 */

import {
  computed,
  defineComponent,
  onMounted,
  ref,
  type ComponentPublicInstance,
  type PropType,
} from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import CloseIcon from "@/components/icons/CloseIcon.vue";
import { hotkeys, type Hotkey } from "@/utils/hotkeys";
import { getKeyboardLayout } from "@/utils/keyboard";
import { keymap, type Keymap } from "@/utils/keymap";

type FormInputInstance = InstanceType<typeof FormInput>;

export default defineComponent({
  components: {
    BaseButton,
    CloseIcon,
    FormInput,
  },
  props: {
    value: {
      type: Array as PropType<Hotkey | null>,
      default: null,
      description: "The hotkey to display; changes are emitted, not applied.",
    },
    size: {
      type: String as PropType<"caption" | "body">,
      default: "body",
      description: "The input size, forwarded to FormInput.",
    },
  },
  emits: ["update:value"],
  setup(props, { emit }) {
    const hotkeyInput = ref<ComponentPublicInstance<FormInputInstance> | null>(
      null
    );
    // Controlled: the parent owns the hotkey; captures only emit.
    const registeredHotkey = computed(() => props.value);
    const capturingModifiers = ref<Record<Keymap.Key, boolean>>({});
    // The active keyboard layout, used to display keys as the user's layout
    // labels them: hotkeys are stored by physical position (named after US
    // QWERTY), so e.g the stored ";" key is the one labeled "M" on French
    // AZERTY. Null until loaded or when the Keyboard API is unavailable, in
    // which case the display falls back to the US QWERTY names.
    const keyboardLayout = ref<Keymap.Layout | null>(null);

    const SymbolicCapturingHotkey = computed((): string => {
      const keys: Keymap.Key[] = [
        ...keymap.keyModifiers.filter(
          (modifier) => capturingModifiers.value[modifier]
        ),
      ];
      const hotkey =
        hotkeys.format.stringify(
          keys,
          keymap.formatSymbol,
          keymap.separatorSpace
        ) || "";
      return hotkey;
    });

    const SymbolicRegisteredHotkey = computed((): string => {
      if (!registeredHotkey.value) return "";
      const values = keymap.convertKeysForLayout(
        registeredHotkey.value,
        keyboardLayout.value
      );
      return values ? hotkeys.format.join(values, keymap.separatorSpace) : "";
    });

    const resetCapture = () => {
      // Reset the capturing modifiers
      keymap.keyModifiers.forEach(
        (modifier) => (capturingModifiers.value[modifier] = false)
      );
    };

    const onKeyPressed = (event: KeyboardEvent) => {
      // Ignore if the key is being composed
      if (event.isComposing) {
        return;
      }
      // Check if the key exists in the keymap; ignore if not
      const key: Keymap.Key | null = keymap.convertKeyboardEvent(event);
      if (!key) {
        window.console.warn(
          `Key with name "${event.key}" and code "${event.code}" is not supported for keyboard shortcuts`
        );
        return null;
      }
      if (keymap.isKeyModifier(key)) {
        // If the key is a modifier, update the list of currently pressed modifiers
        capturingModifiers.value[key] = true;
      } else if (keymap.isKeyPunctuation(key)) {
        // Punctuation keys are captured by physical position, which cannot be
        // matched reliably across keyboard layouts (e.g the ";" position is
        // the "M" key on French AZERTY) — neither by Electron's layout-naive
        // accelerator API nor by the in-app matcher. Ignore them and keep
        // capturing.
        return;
      } else {
        // Otherwise, a "normal" key has been pressed.
        //
        // In this case, we want to freeze and capture the hotkey.

        // Blur the input to prevent the user from typing in it
        hotkeyInput.value?.blur();
        // Compile the list of keys that are being captured
        const keys: Keymap.Key[] = [
          ...keymap.keyModifiers.filter(
            (modifier) => capturingModifiers.value[modifier]
          ),
          key,
        ];
        // Emit the captured hotkey to the parent
        updateHotkey(keys);
      }
    };

    //
    // The KeyboardEvent is composed of mainly two parts:
    // - A key (Shift, ", Control etc.)
    // - A code (KeyB, LeftShift etc.)
    //
    // The code represents the actual key being pressed or the location of the key
    // on the keyboard. The key on the other hand represents the final composition.
    //
    // For example, pressing SHIFT and the quote key (') is used to create a double
    // quote on a QWERTY keyboard.
    //
    // In that scenario:
    //   - SHIFT: key=Shift, code=ShiftLeft
    //   - ': key=", code=Quote
    //
    // Note here that the key for the Quote key being pressed is a double quote ("), the
    // resulting composition of the combination.
    const onKeyReleased = (event: KeyboardEvent) => {
      // Ignore if the key is being composed
      if (event.isComposing) {
        return;
      }
      // Check if the key exists in the keymap.
      //
      // If not, it cannot be taken into account for keyboard shortcuts, as not supported
      const key = keymap.convertKeyboardEvent(event);
      if (!key) {
        window.console.warn(
          `Key with name "${event.key}" and code "${event.code}" is not supported for keyboard shortcuts`
        );
        return;
      }
      // If the key is a modifier, update the list of capturing modifiers
      if (keymap.isKeyModifier(key)) {
        capturingModifiers.value[key] = false;
      }
    };

    const updateHotkey = (keys: Keymap.Key[]) => {
      // Never allow Enter as the only key (reserved for confirming/activating); modifiers+Enter is allowed
      const isOnlyEnter = keys.length === 1 && keys[0] === keymap.keyEnter;
      if (isOnlyEnter) return;

      emit("update:value", keys);
      resetCapture();
    };

    const clear = () => {
      emit("update:value", null);
    };

    // Lifecycle
    onMounted(async () => {
      // Reset the modifiers properly
      resetCapture();

      keyboardLayout.value = await getKeyboardLayout();
    });

    // Return everything that should be available in the template
    return {
      hotkeyInput,
      registeredHotkey,
      SymbolicCapturingHotkey,
      SymbolicRegisteredHotkey,
      onKeyPressed,
      onKeyReleased,
      clear,
    };
  },
});
</script>

<style lang="scss" scoped>
.hotkey-capture {
  display: flex;
  align-items: center;
  gap: 8px;
}
</style>
